import axios from 'axios';
import { getApiBaseUrl } from '../config/runtime';
import { authStorage } from './authStorage';
import { LocationService } from './LocationService';

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds for normal operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate instance for long-running AI operations
const aiLongRunningAPI = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 120000, // 2 minutes for AI processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Token-refresh coordination (shared across all axios instances) ────────
let _isRefreshing = false;
let _refreshQueue = [];

const _processQueue = (error, token = null) => {
  _refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  _refreshQueue = [];
};

const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/google', '/auth/apple'];
const _isAuthPath = (url) => AUTH_PATHS.some((p) => url?.includes(p));

async function _attemptTokenRefresh() {
  const refreshToken = await authStorage.getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  // Use a bare axios call so this request does NOT pass through our interceptor
  const res = await axios.post(`${getApiBaseUrl()}/auth/refresh`, {
    refresh_token: refreshToken,
  });

  const { access_token, refresh_token: newRefresh } = res.data?.data ?? {};
  if (!access_token) throw new Error('Refresh response missing access_token');

  await authStorage.setToken(access_token);
  if (newRefresh) await authStorage.setRefreshToken(newRefresh);

  return access_token;
}

// ── Interceptor setup ─────────────────────────────────────────────────────
const setupInterceptors = (instance) => {
  // Request: attach current access token
  instance.interceptors.request.use(
    async (config) => {
      const token = await authStorage.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response: handle errors, with 401-refresh logic
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;
      const url = originalRequest?.url || '';
      const method = (originalRequest?.method || '').toLowerCase();
      const code = error?.code;

      // Silence expected cancellation / timeout noise
      if (code === 'ECONNABORTED' || code === 'ERR_CANCELED' || error?.message === 'canceled' || error?.name === 'CanceledError') {
        return Promise.reject(error);
      }

      // ── 401 → try refresh, then retry ───────────────────────────────
      if (status === 401 && originalRequest && !originalRequest._authRetry && !_isAuthPath(url)) {
        // Another call is already refreshing – queue this request
        if (_isRefreshing) {
          return new Promise((resolve, reject) => {
            _refreshQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          });
        }

        originalRequest._authRetry = true;
        _isRefreshing = true;

        try {
          const newToken = await _attemptTokenRefresh();
          _processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (refreshErr) {
          _processQueue(refreshErr, null);
          // Refresh failed – force full logout
          await authStorage.removeToken();
          await authStorage.removeRefreshToken();
          window.dispatchEvent(new CustomEvent('auth:force-logout'));
          return Promise.reject(refreshErr);
        } finally {
          _isRefreshing = false;
        }
      }

      // Treat 404 for project images as empty dataset
      if (status === 404 && method === 'get' && url.includes('/projects/') && url.includes('/images')) {
        return Promise.resolve({
          data: { data: { images: [] } },
          status: 200,
          statusText: 'OK',
          headers: error?.response?.headers || {},
          config: originalRequest,
          request: error?.request,
        });
      }

      // Suppress 404 logging for /mine endpoint
      if (status === 404 && method === 'get' && url.includes('/mine')) {
        return Promise.reject(error);
      }

      // Billing: insufficient tokens
      if (status === 402) {
        try {
          const detail = error?.response?.data?.detail || error?.response?.data || {};
          window.dispatchEvent(new CustomEvent('billing:insufficient_tokens', { detail }));
        } catch (_) {}
        return Promise.reject(error);
      }

      console.error('API Error:', error.response?.status, error.response?.data);
      return Promise.reject(error);
    }
  );
};

setupInterceptors(api);
setupInterceptors(aiLongRunningAPI);

// Project APIs (using real /projects endpoints)
export const projectAPI = {
  getAll: (params = {}) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  getActive: () => api.get('/projects/active'),
  getPublic: (params = {}) => api.get('/projects/public', { params }),
  getFinished: (params = {}) => api.get('/projects/finished', { params }),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  updateStatus: (id, statusData) => api.patch(`/projects/${id}/status`, statusData),
  delete: (id) => api.delete(`/projects/${id}`),
  getOriginal: (id) => api.get(`/projects/${id}/original`),
  getImages: (id, params = {}) => api.get(`/projects/${id}/images`, { params }),
  join: (id) => api.post(`/projects/${id}/join`),
  leave: (id) => api.post(`/projects/${id}/leave`),
  getParticipants: (id) => api.get(`/projects/${id}/participants`),
  getUserContext: (id) => api.get(`/projects/${id}/user-context`),
  getActiveWebSocketUsers: (id) => api.get(`/projects/${id}/active-websocket-users`),
  getLeaderboard: (id) => api.get(`/projects/${id}/leaderboard`),
  getLive: (params = {}) => api.get('/projects/live', { params }),
  getUpcoming: (params = {}) => api.get('/projects/upcoming', { params }),
  getObjectiveContributors: (id) => api.get(`/projects/${id}/objectives/contributors`),
  broadcastShout: (id, message) => api.post(`/projects/${id}/shout`, { message }),
  createRematch: (id) => api.post(`/projects/${id}/rematch`),
  getRematchStatus: (id) => api.get(`/projects/${id}/rematch-status`),
  report: (id, data) => api.post(`/projects/${id}/reports`, data),
  addObjective: (id, objectiveData) => api.post(`/projects/${id}/objectives`, objectiveData),
  batchUpdateObjectives: (id, payload) => {
    const body = Array.isArray(payload) ? { changes: payload } : payload;
    return api.post(`/projects/${id}/objectives/batch`, body);
  },
  removeObjective: (projectId, objectiveId) => api.delete(`/projects/${projectId}/objectives/${objectiveId}`),
  // Station arrival (live geofence): one-shot report that the visitor's
  // position crossed into the active station's radius. Server dedupes.
  reportStationArrival: (projectId, objectiveId, data) =>
    api.post(`/projects/${projectId}/objectives/${objectiveId}/arrival`, data),
  restart: (id) => api.post(`/projects/${id}/restart`),
  addCollaborator: (id, data) => api.post(`/projects/${id}/collaborators`, data),
  // "Join my run" — companion join codes (QR from inside the session)
  createJoinCode: (id) => api.post(`/projects/${id}/join-code`),
  getJoinCodeQr: (id, code, base) =>
    api.get(`/projects/${id}/join-code/qr.png`, {
      params: { code, base },
      responseType: 'blob',
    }),
  redeemJoinCode: (code) => api.post(`/projects/join-code/${code}/redeem`),
  removeCollaborator: (id, userId) => api.delete(`/projects/${id}/collaborators/${userId}`),
  getEvents: (id, params = {}) => api.get(`/projects/${id}/events`, { params }),
};

// Tracking APIs
export const trackAPI = {
  start: (projectId, data = {}) => api.post(`/projects/${projectId}/tracks/start`, data),
  ingestPoints: (projectId, trackId, batch) => api.post(`/projects/${projectId}/tracks/${trackId}/points`, batch),
  finish: (projectId, trackId) => api.post(`/projects/${projectId}/tracks/${trackId}/finish`),
  getCurrent: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.chatId) searchParams.append('chat_id', params.chatId);
    const query = searchParams.toString();
    const suffix = query ? `?${query}` : '';
    return api.get(`/projects/${projectId}/tracks/current${suffix}`);
  },
  getLast: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.chatId) searchParams.append('chat_id', params.chatId);
    const query = searchParams.toString();
    const suffix = query ? `?${query}` : '';
    return api.get(`/projects/${projectId}/tracks/last${suffix}`);
  },
  getTrack: (trackId, params = { simplified: true }) => api.get(`/projects/tracks/${trackId}`, { params })
};

// ✅ NEW: Explore APIs for browsing pre-made projects/trails
export const exploreAPI = {
  // Get all explore projects with search and filtering
  getAll: (params = {}) => api.get('/explore', { params }),
  
  // Get explore projects near user's location
  getNearby: (lat, lng, radius = 25, limit = 20) => 
    api.get('/explore/nearby', { params: { lat, lng, radius, limit } }),
  
  // Get detailed information about a specific explore project
  getById: (id, userLocation = null) => {
    const params = userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : {};
    return api.get(`/explore/${id}`, { params });
  },
  
  // Clone an explore project to user's personal projects
  clone: (id, data = {}) => api.post(`/explore/${id}/clone`, data),

  // One-call play entry: clone-or-resume + session (Scan to Play deep link).
  // mode 'resume'|'fresh' answers the continue-or-start-over choice the
  // backend returns for an explicitly ended, partially completed run.
  play: (id, mode) =>
    api.post(`/explore/${id}/play${mode ? `?mode=${encodeURIComponent(mode)}` : ''}`),
  
  // Create new explore project (requires editor permissions)
  create: (data) => api.post('/explore', data),
  
  // Update explore project (owner/admin only)
  update: (id, data) => api.put(`/explore/${id}`, data),
  
  // Delete explore project (owner/admin only)
  delete: (id) => api.delete(`/explore/${id}`),
  
  // Search explore projects with advanced filters
  search: (query, filters = {}) => 
    api.get('/explore', { params: { search: query, ...filters } }),

  // Search for a location by address or coordinates
  searchLocation: (query, searchType = 'address') => 
    api.post('/explore/search-location', { query, search_type: searchType }),
  
  // Get images for an explore project (template + all cloned project images)
  getImages: (id, params = {}) => api.get(`/explore/${id}/images`, { params })
};

// ✅ NEW: Reviews API
export const reviewAPI = {
  getSummary: (projectId) => api.get(`/reviews/projects/${projectId}/summary`),
  getList: (projectId, params = {}) => api.get(`/reviews/projects/${projectId}`, { params }),
  getMy: (projectId) => api.get(`/reviews/projects/${projectId}/my`),
  create: (projectId, data) => api.post(`/reviews/projects/${projectId}`, data),
  update: (reviewId, data) => api.patch(`/reviews/${reviewId}`, data),
  remove: (reviewId) => api.delete(`/reviews/${reviewId}`),
  getTags: () => api.get('/reviews/tags'),
  // Template aggregation endpoints
  getTemplateSummary: (templateId) => api.get(`/reviews/templates/${templateId}/summary`),
  getTemplateList: (templateId, params = {}) => api.get(`/reviews/templates/${templateId}`, { params }),
  // Session-level review endpoints
  getChatReview: (chatId) => api.get(`/reviews/chats/${chatId}`),
  getSessionReviews: (projectId) => api.get(`/reviews/projects/${projectId}/sessions`),
};

// ✅ NEW: Saved Lists APIs for managing user favorites
export const savedAPI = {
  // Get user's saved lists
  getLists: (params = {}) => api.get('/saved/lists', { params }),
  
  // Get specific saved list with items
  getList: (listId, includeItems = true) => 
    api.get(`/saved/lists/${listId}`, { params: { include_items: includeItems } }),
  
  // Create new saved list
  createList: (data) => api.post('/saved/lists', data),
  
  // Update saved list
  updateList: (listId, data) => api.put(`/saved/lists/${listId}`, data),
  
  // Delete saved list
  deleteList: (listId) => api.delete(`/saved/lists/${listId}`),
  
  // Add project to saved list
  addItem: (listId, projectId) => 
    api.post(`/saved/lists/${listId}/items`, { projectId }),
  
  // Remove project from saved list
  removeItem: (listId, projectId) => 
    api.delete(`/saved/lists/${listId}/items/${projectId}`),
  
  // Check if project is saved (and in which lists)
  getProjectStatus: (projectId) => 
    api.get(`/saved/project/${projectId}/status`),
  
  // Check saved status for multiple projects (bulk)
  getProjectsStatusBulk: (projectIds) => 
    api.get('/saved/project/bulk-status', { 
      params: { project_ids: projectIds.join(',') } 
    })
};

// Image upload for nature photos (simplified from document APIs)
export const imageAPI = {
  upload: (formData) => api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Nature AI Backend APIs - Simplified for Okio
export const aiAPI = {
  // System health check
  getStatus: () => api.get('/health'),
  
  // Nature-focused AI features (future)
  identifyPlant: (imageData) => {
    console.warn('Plant identification endpoint not implemented yet');
    return Promise.resolve({ species: null, confidence: 0 });
  },
};

// User profile APIs (nature-focused)
export const userAPI = {
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
};

// Admin APIs
export const adminAPI = {
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getStats: () => api.get('/admin/stats'),
  adjustUserTokens: (userId, data) => api.post(`/admin/users/${userId}/tokens`, data),
  getTokenLedger: (params = {}) => api.get('/admin/token-ledger', { params }),
  getVouchers: (params = {}) => api.get('/admin/vouchers', { params }),
  createVoucher: (data) => api.post('/admin/vouchers', data),
  updateVoucher: (voucherId, data) => api.patch(`/admin/vouchers/${voucherId}`, data),
  getReports: (params = {}) => api.get('/admin/reports', { params }),
  updateReport: (reportId, data) => api.patch(`/admin/reports/${reportId}`, data),
  deleteReport: (reportId) => api.delete(`/admin/reports/${reportId}`),
  getChats: (params = {}) => api.get('/admin/chats', { params }),
  getChat: (chatId) => api.get(`/admin/chats/${chatId}`),
  getChatMessages: (chatId) => api.get(`/admin/chats/${chatId}/messages`),
};

// Authentication APIs
export const authAPI = {
  searchUsers: (query, limit = 10) => api.get('/auth/users/search', { params: { q: query, limit } }),
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  // Scan-to-Play: mint an ephemeral guest identity for a public starter —
  // or, with { joinCode }, as a companion on an existing run ("join my run")
  createGuestSession: (params) =>
    api.post(
      '/auth/guest',
      typeof params === 'string' ? { starterId: params } : params
    ),
  logout: () => api.post('/auth/logout'),
  checkUsernameAvailability: (username) => api.get(`/auth/username/check/${username}`),
  getUserByUsername: (username) => api.get(`/auth/users/${username}`),
  getPublicUserProjects: (username, params = {}) => api.get(`/auth/users/${username}/projects`, { params }),
  refreshToken: (refresh_token) => api.post('/auth/refresh', { refresh_token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (profileData) => api.put('/auth/me', profileData),
  deleteAccount: () => api.delete('/auth/me'),
  getOAuthConfig: () => api.get('/auth/oauth/config'),
  // OAuth APIs
  googleOAuthWeb: () => {
    // Redirect to backend OAuth endpoint
    window.location.href = `${api.defaults.baseURL}/auth/google`;
  },
  googleOAuthNative: (idToken, serverAuthCode = null, inviteCode = null) =>
    api.post('/auth/google/native', { idToken, serverAuthCode, inviteCode }),
  appleOAuthNative: (identityToken, user) => api.post('/auth/apple/native', { identityToken, user }),
};

// Partner pages (Scan to Play venues)
export const partnerAPI = {
  getPage: (slug) => api.get(`/partners/${slug}`),
  getGallery: (slug, limit = 24) => api.get(`/partners/${slug}/gallery`, { params: { limit } }),
  getReviews: (slug, limit = 6) => api.get(`/partners/${slug}/reviews`, { params: { limit } }),
  // Which venue owns a starter — powers the session-page venue backlink
  getByStarter: (starterId) => api.get(`/partners/by-starter/${starterId}`),
  getStats: (slug, days = null) =>
    api.get(`/partners/${slug}/stats`, { params: days ? { days } : {} }),
  // Admin dashboard (partner self-serve is a later sprint)
  getGalleryAdmin: (slug, limit = 60) => api.get(`/partners/${slug}/gallery/all`, { params: { limit } }),
  moderatePhoto: (slug, mediaId, action) => api.post(`/partners/${slug}/gallery/${mediaId}/moderate`, { action }),
  // QR is auth-gated, so fetch as blob and objectURL it into <img>/downloads
  getStarterQr: (slug, starterId, base) =>
    api.get(`/partners/${slug}/starters/${starterId}/qr.png`, {
      params: { base },
      responseType: 'blob',
    }),
  // Venue QR → /p/{slug} (the "what shall we do today?" poster)
  getVenueQr: (slug, base) =>
    api.get(`/partners/${slug}/qr.png`, { params: { base }, responseType: 'blob' }),
  // payload: { auto_instruction_images?: bool, is_public?: bool }
  updateStarterSettings: (slug, starterId, payload) =>
    api.patch(`/partners/${slug}/starters/${starterId}/settings`, payload),
  // Station drop-off funnel (aggregate-only, suppressed under min sample)
  getStarterFunnel: (slug, starterId) =>
    api.get(`/partners/${slug}/starters/${starterId}/funnel`),
  // Partner logins (plan_b2b_self_serve.md §4.1): the venue's own account
  getMine: () => api.get('/partners/mine'),
  createSelfServe: (payload) => api.post('/partners/self-serve', payload),
  // Venue-facing knobs only (owner or admin) — tier/budgets stay admin
  updateVenueSettings: (slug, payload) =>
    api.patch(`/partners/${slug}/venue-settings`, payload),
  // Wizard v0: structured guide-intake requests (concierge fulfillment)
  createGuideRequest: (slug, payload) =>
    api.post(`/partners/${slug}/guide-requests`, payload),
  listGuideRequests: (slug) => api.get(`/partners/${slug}/guide-requests`),
  // Partner lifecycle (admin CRUD — /admin/partners)
  list: () => api.get('/partners'),
  create: (payload) => api.post('/partners', payload),
  update: (slug, payload) => api.patch(`/partners/${slug}`, payload),
  // DIY guide pipeline: owner's own unattached starters, for "add to my page"
  listAttachableStarters: (slug) =>
    api.get(`/partners/${slug}/attachable-starters`),
  attachStarter: (slug, starterId) =>
    api.post(`/partners/${slug}/starters`, { starterId }),
  detachStarter: (slug, starterId) =>
    api.delete(`/partners/${slug}/starters/${starterId}`),
};

// Search APIs - Simplified for nature content
export const searchAPI = {
  projects: (query, filters = {}) => api.get('/search/projects', { params: { q: query, ...filters } }),
  nature: (query, projectId) => api.post('/search/nature', { query, projectId }),
};

// Chat API
export const chatAPI = {
  getProjectChats: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.search) searchParams.append('search', params.search);
    if (params.cursor && params.cursor.updatedAt && params.cursor.id) {
      // Pass cursor fields for seek pagination
      searchParams.append('cursorUpdatedAt', params.cursor.updatedAt);
      searchParams.append('cursorId', params.cursor.id);
    }

    return api.get(`/chats/project/${projectId}?${searchParams.toString()}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  },
  getByProject: (projectId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.search) searchParams.append('search', params.search);
    
    return api.get(`/chats/project/${projectId}?${searchParams.toString()}`);
  },
  getById: (chatId) => api.get(`/chats/${chatId}`),
  getChat: (chatId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.include_messages !== undefined) {
      // Convert boolean to string for URLSearchParams (FastAPI will parse it back to boolean)
      searchParams.append('include_messages', String(params.include_messages));
    }
    if (params.messages_limit) {
      searchParams.append('messages_limit', String(params.messages_limit));
    }
    
    const url = `/chats/${chatId}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    return api.get(url);
  },
  getChatMessages: (chatId, params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    
    return api.get(`/chats/${chatId}/messages?${searchParams.toString()}`);
  },
  createChat: (data) => {
    return api.post('/chats', data).then(response => {
      return response;
    });
  },
  create: (data) => {
    return api.post('/chats', data).then(response => {
      return response;
    });
  },
  addMessage: (chatId, data) => api.post(`/chats/${chatId}/messages`, data),
  sendAIMessage: (chatId, content) => {
    console.log('Sending AI message to chat:', chatId, 'Content:', content);
    return api.post(`/chats/${chatId}/ai-message`, { content });
  },
  startNewSession: (projectId) => api.post(`/chats/project/${projectId}/session/new`),
  archiveCurrentSession: (projectId) => api.post(`/chats/project/${projectId}/session/archive`),
  streamLearnCard: (data) => {
    console.log('Starting learn card streaming:', data);
    return api.post(`/chats/${data.messageId}/learn-card`, {
      cardId: data.cardId,
      cardTitle: data.cardTitle,
      cardSubtitle: data.cardSubtitle
    });
  },
  updateChat: (chatId, data) => api.patch(`/chats/${chatId}`, data),
  deleteChat: (chatId) => api.delete(`/chats/${chatId}`),
  getMineForProject: (projectId) => api.get(`/chats/project/${projectId}/mine`),
  // ThingsDB confirmation endpoints
  getDetectionCandidates: (detectionId) => api.get(`/thingsdb/detections/${detectionId}/candidates`),
  getDetectionStatus: (detectionId) => api.get(`/thingsdb/detections/${detectionId}/status`),
  confirmDetection: (detectionId, data) => api.post(`/thingsdb/detections/${detectionId}/confirm`, data),
  rejectDetection: (detectionId, data) => api.post(`/thingsdb/detections/${detectionId}/reject`, data),
  getThingTimeline: (thingId, projectId) => api.get(`/thingsdb/things/${thingId}/timeline`, { params: { project_id: projectId } })
};

// Objective matches API
export const objectiveMatchAPI = {
  confirm: (matchId, data) => api.post(`/objective-matches/${matchId}/confirm`, data),
  dismiss: (matchId, data) => api.post(`/objective-matches/${matchId}/dismiss`, data),
  get: (matchId) => api.get(`/objective-matches/${matchId}`),
  getStatuses: (ids) => api.get(`/objective-matches/status`, { params: { ids: Array.isArray(ids) ? ids.join(',') : ids } }),
  getProjectMatches: (projectId) => api.get(`/objective-matches/project/${projectId}`),
};

// Interactive cards state
export const interactiveCardsAPI = {
  markHandled: ({ messageId, cardId, state }) => api.post(`/interactive-cards/mark-handled`, { messageId, cardId, state }),
  explain: ({ messageId, cardId }) => api.post(`/interactive-cards/explain`, { messageId, cardId }),
  searchLocations: ({ searchIntent, userLocation }) => api.post(`/interactive-cards/location-search`, { search_intent: searchIntent, user_location: userLocation })
};

export const billingAPI = {
  getStatus: () => api.get('/billing/status'),
  redeemVoucher: (code) => api.post('/billing/vouchers/redeem', { code })
};

export const paymentsAPI = {
  createStripeCheckout: (payload) => api.post('/payments/stripe/create-checkout', payload),
  createStripePortal: (payload) => api.post('/payments/stripe/create-portal', payload)
};

// ThingsDB API for Discover page (detections, observations, things)
export const thingsAPI = {
  // Get items (things, observations, or detections) with geo-filtering
  // layer: 'things' | 'observations' | 'detections'
  getItems: (params = {}) => api.get('/thingsdb/things', { params }),
  
  // Get a single detection by ID
  getDetection: (detectionId) => api.get(`/thingsdb/detections/${detectionId}`),
  
  // Get thing detail by ID
  getThing: (thingId) => api.get(`/thingsdb/things/${thingId}`),
  
  // Get observation detail by ID
  getObservation: (observationId) => api.get(`/thingsdb/observations/${observationId}`),
  
  // Get detections for a specific thing
  getThingDetections: (thingId, limit = 100) => api.get(`/thingsdb/things/${thingId}/detections`, { params: { limit } }),
  
  // Health check
  health: () => api.get('/thingsdb/health'),
};


// Helper function to handle API errors
export const handleAPIError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    return {
      status,
      message: data.message || 'An error occurred',
      details: data.details || null,
      errors: data.errors || null,
    };
  } else if (error.request) {
    return {
      status: 0,
      message: 'Network error - please check your connection',
      details: 'No response from server',
    };
  } else {
    return {
      status: 0,
      message: 'An unexpected error occurred',
      details: error.message,
    };
  }
};

// Query key factories for React Query
export const createQueryKey = {
  projects: () => ['projects'],
  project: (id) => ['projects', id],
  activeProject: () => ['projects', 'active'],
  liveProjects: () => ['projects', 'live'],
  upcomingProjects: () => ['projects', 'upcoming'],
  user: () => ['user'],
  health: () => ['health'],
  chats: (projectId) => ['project-chats', projectId],
  chat: (chatId) => ['chat', chatId],
  projectState: (projectId) => ['project-state', projectId],
  savedLists: () => ['saved-lists'],
  savedList: (id) => ['saved-lists', id],
  projectSavedStatus: (id) => ['project-saved-status', id],
  reviewsSummary: (projectId) => ['reviews-summary', projectId],
  reviewsList: (projectId, params) => ['reviews-list', projectId, params],
  myReview: (projectId) => ['my-review', projectId],
};


// Project state management
export const projectStateAPI = {
  // Get project state
  getProjectState: async (projectId) => {
    const response = await api.get(`/projects/${projectId}/state`);
    return response.data;
  },

  // Update project state (merges with existing state)
  updateProjectState: async (projectId, stateUpdate) => {
    const response = await api.post(`/projects/${projectId}/state`, stateUpdate);
    return response.data;
  },

  // Reset project state to empty
  resetProjectState: async (projectId) => {
    const response = await api.delete(`/projects/${projectId}/state`);
    return response.data;
  }
};

// Objective APIs
export const objectiveAPI = {
  getAll: (params = {}) => api.get('/objectives', { params }),
  getById: (id) => api.get(`/objectives/${id}`),
  create: (data) => api.post('/objectives', data),
  update: (id, data) => api.put(`/objectives/${id}`, data),
  delete: (id) => api.delete(`/objectives/${id}`),
};

// Export default instance for other uses
// ✅ NEW: Environment APIs for managing project environments
export const environmentAPI = {
  // Get all environments with search and filtering
  getAll: (params = {}) => api.get('/environments', { params }),
  
  // Get specific environment by ID
  getById: (id) => api.get(`/environments/${id}`),
  
  // Create new custom environment
  create: (data) => api.post('/environments', data),
  
  // Update environment (only if user created it)
  update: (id, data) => api.put(`/environments/${id}`, data),
  
  // Delete environment (only if user created it)
  delete: (id) => api.delete(`/environments/${id}`),
  
  // Search environments
  search: (query, params = {}) => 
    api.get('/environments', { params: { search: query, ...params } })
};


// ============ LOCATION UTILITIES ============

export const locationAPI = {
  // Get user's current location (browser API wrapper)
  getCurrentLocation: () => {
    return LocationService.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 })
      .then((pos) => ({ lat: pos.lat, lng: pos.lng }));
  }
};

export default api; 
